import { Injectable, Injector, signal } from "@angular/core";
import { Firestore } from "@angular/fire/firestore";
import { Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { PrivateBrowsingDetectorService } from "@core/private-browsing-detector.service";
import { doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { OneSignal } from "onesignal-ngx";
import { BehaviorSubject, defer, firstValueFrom, forkJoin, from, Observable, of, Subject, throwError, timer } from "rxjs";
import { catchError, delayWhen, finalize, map, retryWhen, scan, switchMap, take, takeUntil, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { CommonVariable } from "../app.constants";
import { CommonService } from "./common/common.service";
import { NotificationsService } from "./notifications.service";
import { RequestBodyService } from "./request-body.service";
import { UserManagementService } from "./user-management.service";

/** Result of OneSignal opt-in + push prize award chain */
export type PushOptInResultStatus =
  | "success"
  | "already_issued"
  | "sdk_unavailable"
  | "error";

export interface PushOptInResult {
  status: PushOptInResultStatus;
  raw?: unknown;
}

export interface PushOptInOptions {
  skipPrizeAward?: boolean;
}

/** AC4: already-issued accounts must not call the prize SQL again. */
export function shouldSkipPushPrizeAward(alreadyIssuedOnAccount: boolean): boolean {
  return alreadyIssuedOnAccount === true;
}

// Interface for OneSignal configuration
export interface AppConfig {
  appId: string;
  autoResubscribe: boolean;
  autoRegister: boolean;
  notifyButton: {
    enable: boolean;
  };
  welcomeNotification: {
    disable: boolean;
  };
  subdomainName: string;
  serviceWorkerParam: {
    scope: string;
  };
  serviceWorkerPath: string;
}

@Injectable({
  providedIn: "root",
})
export class OneSignalService {
  private config: any;
  public showOneSignalNotificationUI$ = new BehaviorSubject(true);
  public isDismissed = signal(false);
  private isOneSignalInitialized = false;
  private subscriptionId!: string;
  private _oneSignalRejectionHandler: ((event: PromiseRejectionEvent) => void) | null = null;
  private preferredCurrency: string;
  private destroy$ = new Subject<void>();
  private reconcileInFlight = false;
  private subscriptionChangeListenerAttached = false;

  constructor(
    private firestore: Firestore,
    private oneSignal: OneSignal,
    private router: Router,
    private notificationsService: NotificationsService,
    private requestBodyService: RequestBodyService,
    private userManagementService: UserManagementService,
     private injector: Injector,
    private commonService: CommonService,
    private privateBrowsingDetector: PrivateBrowsingDetectorService,
  ) {
    this.preferredCurrency = this.userManagementService?.selectedCurrency;
    // Initialize the default configuration
    this.config = {
      appId: environment?.oneSignalAppId,
      autoResubscribe: true,
      autoRegister: true,
      notifyButton: { enable: false },
      welcomeNotification: {
        disable: false  // Optional property
      },
      subdomainName: "lottosocial",
      serviceWorkerParam: { scope: "/push/onesignal" },
      serviceWorkerPath: "/push/onesignal/OneSignalSDKWorker.js",
    } as any;

  }

  // Initialize OneSignal and handle subscription changes
  public init(): void {
    if (this.isOneSignalInitialized) return;
    if (window?.location?.origin !== environment?.baseURL) return;

    // Check if IndexedDB is available before initializing OneSignal
    // OneSignal SDK requires IndexedDB to function properly
    if (!this.privateBrowsingDetector.isIndexedDBAvailable()) {
      console.warn(
        'OneSignal initialization skipped: IndexedDB is not available. ' +
        'This typically occurs in private browsing mode or when browser storage is disabled.'
      );
      return;
    }

    // Guard against unhandled "No subscription" rejections thrown internally by the
    // OneSignal SDK's SessionManager when the device has no active push subscription
    // (common on iOS Safari). Store the handler so it can be removed on destroy.
    this._oneSignalRejectionHandler = (event: PromiseRejectionEvent) => {
      const message = event?.reason?.message || String(event?.reason || '');
      if (message === 'No subscription') {
        event.preventDefault();
      }
    };
    window.addEventListener('unhandledrejection', this._oneSignalRejectionHandler);

    try {
      this.isOneSignalInitialized = true;
      this.oneSignal.init(this.config).then(() => {
        // Register the v16 change listener even when not yet opted in, so
        // slidedown / browser-prompt opt-in can still reconcile identity.
        // Guard against duplicate registration if init() is re-entered.
        if (!this.subscriptionChangeListenerAttached) {
          this.subscriptionChangeListenerAttached = true;
          this.oneSignal?.User?.PushSubscription?.addEventListener("change", (change) => {
            if (change?.current?.optedIn) {
              const subscriptionId = change?.current?.id;
              if (subscriptionId) {
                this.notificationsService.processCardPayment(subscriptionId);
              }
              this.reconcileIdentityAndSubscription();
            }
          });
        }

        // Only proceed with subscription-dependent logic if the user is actually opted in.
        // This prevents OneSignal's internal session upsert from firing for unsubscribed
        // users (e.g. iOS Safari users who never granted push permission).
        const isSubscribed = this.oneSignal?.User?.PushSubscription?.optedIn;
        if (!isSubscribed) {
          this.isOneSignalInitialized = false;
          return;
        }

        this.promptForValidPages();

        // Fetch tags via direct OneSignal SDK access
        // Note: getTags() returns a plain object synchronously, not a Promise
        try {
          const tags = (window as Data).OneSignal.User.getTags();
          if (!tags?.currency) {
            this.setUserCurrencyTag(this.preferredCurrency);
          }
        } catch (err) {
          console.error("Error fetching OneSignal tags:", err);
        }

        // Bind external_id + SQL/Firestore token for already opted-in users
        // (slidedown / browser prompt / historical subscribers).
        this.reconcileIdentityAndSubscription();

        this.isOneSignalInitialized = false;
      }).catch((error) => {
        console.error("Error during OneSignal init:", error);
        this.isOneSignalInitialized = false;
      });
    } catch (error) {
      console.error("Error initializing OneSignal:", error);
      this.isOneSignalInitialized = false;
    }
  }


  // Opt the user into push notifications.
  // Incoming chain (subscription id + token/prefs/prize) kept.
  // Returns award result so banner can treat already-issued vs first success.
  public optIn(userId: string, options?: PushOptInOptions): Observable<PushOptInResult> {
    if (!userId) {
      return throwError(() => new Error("User ID required for OneSignal opt-in"));
    }

    // Live Incognito often still exposes window.indexedDB, so the sync availability
    // check alone is not enough — detect private browsing before touching the SDK.
    return from(this.canUseOneSignalSdk()).pipe(
      switchMap((canUseSdk) => {
        if (!canUseSdk) {
          return this.runAwardWithoutOneSignalSdk(userId, options);
        }
        return this.optInWithOneSignalSdk(userId, options);
      }),
      take(1)
    );
  }

  /** Sync IndexedDB + async private-browsing detection before Live SDK opt-in. */
  private async canUseOneSignalSdk(): Promise<boolean> {
    if (!this.isOneSignalRuntimeAvailable()) {
      return false;
    }
    try {
      // Timeout → treat as unavailable so a hung private-mode probe cannot fall
      // through to OneSignal and surface "Something went wrong" (AC7).
      const isPrivate = await Promise.race([
        this.privateBrowsingDetector.detectPrivateBrowsing(),
        new Promise<boolean>((resolve) => {
          setTimeout(() => resolve(true), 1500);
        }),
      ]);
      if (isPrivate) {
        return false;
      }
    } catch {
      // Live: detection failure is safer as SDK-unavailable than a generic error toast.
      return false;
    }
    return true;
  }

  private optInWithOneSignalSdk(
    userId: string,
    options?: PushOptInOptions
  ): Observable<PushOptInResult> {
    // Localhost / non-Live origin cannot init OneSignal (environment.baseURL is
    // https://app.lottosocial.com). Live still uses the full SDK path. Prize SQL
    // without a verified SDK opt-in is only allowed off Live origin.
    const optInFn = this.oneSignal?.User?.PushSubscription?.optIn;
    if (typeof optInFn !== "function") {
      return this.runAwardWithoutOneSignalSdk(userId, options);
    }

    const optInPromise = optInFn.call(this.oneSignal.User.PushSubscription);
    if (!optInPromise || typeof (optInPromise as Promise<unknown>).then !== "function") {
      return this.runAwardWithoutOneSignalSdk(userId, options);
    }

    return from(optInPromise).pipe(
      switchMap(() => this.waitForSubscriptionId()),
      switchMap((subscriptionId) => {
        this.subscriptionId = subscriptionId;
        const deviceId = CommonVariable.FingerprintJSConst || localStorage.getItem("fingerSalt");
        // Fresh Incognito often has no fingerSalt — treat as SDK unavailable on Live
        // instead of throwing → generic "Something went wrong".
        if (!deviceId) {
          return this.runAwardWithoutOneSignalSdk(userId, options);
        }

        const oneSignalId =
          (this.oneSignal?.User as Data)?.onesignalId || this.subscriptionId;
        const oneSignalData = {
          subscription_id: this.subscriptionId,
          token_status: 1,
          onesignal_id: oneSignalId,
        };

        this.commonService.getUid("OneSignalService.optIn").pipe(take(1)).subscribe({
          next: (res) => {
            if (res) {
              this.oneSignal.login(res);
            } else {
              const authService = this.injector.get(AuthService || null);
              authService.errorHandlerLogs("User ID not found during OneSignal opt-in");
            }
          },
          error: (error) => error,
        });
        this.setUserCurrencyTag(this.preferredCurrency);

        // Do not write preferences_settings here — marketing consent is owned by
        // PreferenceComponent / registration persist. Overwriting push:true after
        // opt-in caused opted-out Push toggles to reappear as enabled.
        return this.updateOneSignalToken$(oneSignalData).pipe(
          switchMap(() => this.insertOneSignalSubscriptionId(deviceId, this.subscriptionId)),
          // HATDEV-100046: skip prize SQL when account already awarded; surface PushOptInResult
          switchMap(() =>
            shouldSkipPushPrizeAward(options?.skipPrizeAward === true)
              ? of({ status: "already_issued" as const } as PushOptInResult)
              : this.updatePushNotificationStatus(userId)
          )
        );
      }),
      tap(() => {
        this.showOneSignalNotificationUI$.next(false);
        this.isDismissed.set(true);
      }),
      catchError((error) => {
        console.error("Error during opt-in for user:", error);
        if (this.isAlreadyIssuedPayload(error)) {
          return of({ status: "already_issued" as const, raw: error });
        }
        // Live private browsing / storage failures must not surface as generic errors.
        if (!this.isNonLiveOrigin() && this.isSdkUnavailableError(error)) {
          return of({ status: "sdk_unavailable" as const, raw: error });
        }
        return throwError(() => error);
      }),
      take(1)
    );
  }

  /** Map Incognito / IndexedDB / fingerprint failures to typed sdk_unavailable. */
  private isSdkUnavailableError(error: unknown): boolean {
    const err = error as { message?: unknown; name?: unknown } | null;
    const msg = `${err?.name || ""} ${err?.message || error || ""}`.toLowerCase();
    // Keep this tight — do not treat generic subscription timeouts as private browsing
    // (iOS Safari often surfaces "No subscription" during a normal opt-in).
    // Do NOT map NotAllowedError / "not allowed" here: that is usually the user
    // denying notification permission, not Incognito/SDK unavailability.
    return (
      msg.includes("indexeddb") ||
      msg.includes("private") ||
      msg.includes("incognito") ||
      msg.includes("createobjectstore") ||
      msg.includes("fingerprint") ||
      msg.includes("pushmanager") ||
      msg.includes("push service") ||
      msg.includes("securityerror") ||
      msg.includes("quotaexceeded") ||
      msg.includes("storage")
    );
  }

  /** OneSignal SDK only runs on the configured app origin with IndexedDB. */
  private isOneSignalRuntimeAvailable(): boolean {
    if (typeof window === "undefined") {
      return false;
    }
    if (this.isNonLiveOrigin()) {
      return false;
    }
    if (!this.privateBrowsingDetector.isIndexedDBAvailable()) {
      return false;
    }
    return true;
  }

  /** Localhost / preview hosts — not https://app.lottosocial.com. */
  private isNonLiveOrigin(): boolean {
    if (typeof window === "undefined") {
      return false;
    }
    return window.location?.origin !== environment?.baseURL;
  }

  /** Prize/status path used off Live origin where OneSignal is not initialised. */
  private runAwardWithoutOneSignalSdk(
    userId: string,
    options?: PushOptInOptions
  ): Observable<PushOptInResult> {
    // Live must not award without a verified SDK opt-in (e.g. Incognito / no IndexedDB).
    // Surface a typed result so the banner can show a clear private-browsing toast
    // instead of a generic "Something went wrong" error.
    if (!this.isNonLiveOrigin()) {
      return of({ status: "sdk_unavailable" as const } as PushOptInResult);
    }

    const award$ = shouldSkipPushPrizeAward(options?.skipPrizeAward === true)
      ? of({ status: "already_issued" as const } as PushOptInResult)
      : this.updatePushNotificationStatus(userId);

    return award$.pipe(
      tap(() => {
        this.showOneSignalNotificationUI$.next(false);
        this.isDismissed.set(true);
      }),
      take(1)
    );
  }

  /**
   * Wait for OneSignal PushSubscription.id (can arrive after optIn on iOS/APNs).
   */
  private waitForSubscriptionId(timeoutMs: number = 15000): Observable<string> {
    const existingId = this.oneSignal?.User?.PushSubscription?.id;
    if (existingId) {
      return of(existingId);
    }

    return new Observable<string>((subscriber) => {
      const pushSubscription = this.oneSignal?.User?.PushSubscription as Data;
      let settled = false;

      const finish = (id?: string, error?: Error) => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(timerId);
        try {
          pushSubscription?.removeEventListener?.("change", onChange);
        } catch {
          // ignore cleanup failures
        }
        if (id) {
          subscriber.next(id);
          subscriber.complete();
        } else {
          subscriber.error(error || new Error("Subscription ID not found"));
        }
      };

      const onChange = () => {
        const id = this.oneSignal?.User?.PushSubscription?.id;
        if (id) {
          finish(id);
        }
      };

      const timerId = setTimeout(() => {
        finish(undefined, new Error("Timed out waiting for OneSignal subscription id"));
      }, timeoutMs);

      try {
        pushSubscription?.addEventListener?.("change", onChange);
      } catch (error) {
        finish(undefined, error instanceof Error ? error : new Error("Failed to listen for subscription id"));
      }

      // Re-check in case id arrived between initial read and listener attach
      const immediateId = this.oneSignal?.User?.PushSubscription?.id;
      if (immediateId) {
        finish(immediateId);
      }

      return () => {
        settled = true;
        clearTimeout(timerId);
        try {
          pushSubscription?.removeEventListener?.("change", onChange);
        } catch {
          // ignore cleanup failures
        }
      };
    });
  }

  private isSuccessfulApiResult(res: Data): boolean {
    if (res == null) {
      return false;
    }
    const status = res?.status ?? res?.response?.status ?? res?.[0]?.status;
    if (typeof status === "string" && status.toLowerCase() === "fail") {
      return false;
    }
    return true;
  }


  // Opt the user out of push notifications
  public optOut(): void {
    this.oneSignal?.User?.PushSubscription?.optOut().catch((error) => {
      console.error("Error during opt-out:", error);
    });
  }

  /**
   * Shared-device logout: deactivate stored push token/subscription, then clear
   * OneSignal external_id before Firebase sign-out.
   * Keeps AuthService from depending on SDK details directly.
   * Best-effort with a short timeout so a hung OneSignal.logout()
   * (e.g. preview host where SDK was never inited) cannot block Firebase signOut.
   */
  public async logoutIdentity(): Promise<void> {
    try {
      // OneSignal.init is skipped when origin !== environment.baseURL (preview channels).
      // Calling logout without a completed init can hang indefinitely.
      if (window?.location?.origin !== environment?.baseURL) {
        return;
      }

      // Deactivate CleverTap/SQL + Firestore token before clearing external_id
      // (same pattern as AccountRepositoryService.saveOneSignalDetailsOption(false)).
      await this.deactivateStoredSubscriptionOnLogout();

      if (typeof this.oneSignal?.logout !== "function") {
        return;
      }
      const logoutResult = this.oneSignal.logout();
      if (logoutResult && typeof (logoutResult as Promise<void>).then === "function") {
        await Promise.race([
          logoutResult,
          new Promise<void>((resolve) => setTimeout(resolve, 2000)),
        ]);
      }
    } catch (error) {
      console.error("Error during OneSignal logout:", error);
    }
  }

  /**
   * Best-effort: mark the current device subscription inactive in backend
   * (token_status: 0) and Firestore token_details (subscription_status: false)
   * while auth is still valid. Must run before OneSignal.logout() / signOut().
   */
  private async deactivateStoredSubscriptionOnLogout(): Promise<void> {
    const subscriptionId =
      this.oneSignal?.User?.PushSubscription?.id || this.subscriptionId;
    if (subscriptionId) {
      this.subscriptionId = subscriptionId;
    }

    const deviceId =
      CommonVariable.FingerprintJSConst || localStorage.getItem("fingerSalt");

    if (!subscriptionId && !deviceId) {
      return;
    }

    const deactivate$ = this.commonService.getUid("OneSignalService-logout").pipe(
      take(1),
      switchMap((uid) => {
        if (!uid) {
          return of(null);
        }

        const tokenDeactivate$ = subscriptionId
          ? this.updateOneSignalToken$({
              subscription_id: subscriptionId,
              token_status: 0,
              onesignal_id: subscriptionId,
            }).pipe(catchError(() => of(null)))
          : this.updateOneSignalToken$({ token_status: 0 }, true).pipe(
              catchError(() => of(null))
            );

        const detailsRef = doc(
          this.firestore,
          `users/${uid}/onesignal-push-notifications/details`
        );

        const firestoreDeactivate$ = from(getDoc(detailsRef)).pipe(
          switchMap((snapshot) => {
            const data = snapshot?.data() as Data;
            const tokens = data?.token_details;
            if (!Array.isArray(tokens) || !tokens.length) {
              return of(null);
            }

            let changed = false;
            tokens.forEach((element: Data) => {
              const isMatchingFingerprint = element?.fingerprint_id === deviceId;
              const isMatchingOnesignal = element?.onesignal_id === deviceId;
              const isMatchingSubscription =
                !!subscriptionId &&
                (element?.player_id === subscriptionId ||
                  element?.subscription_id === subscriptionId);

              if (
                isMatchingFingerprint ||
                isMatchingOnesignal ||
                isMatchingSubscription
              ) {
                element.subscription_status = false;
                changed = true;
              }
            });

            if (!changed) {
              return of(null);
            }

            // Same Firestore update path as preference opt-out.
            return this.getV2CloudFunction("updatefirestoredoc", {
              path_id: "f4r0yr15mqEsGYPl0EHr",
              doc_data: data,
              method: "update",
            }).pipe(
              take(1),
              map(() => null),
              catchError(() => of(null))
            );
          }),
          catchError(() => of(null))
        );

        return forkJoin([tokenDeactivate$, firestoreDeactivate$]).pipe(
          map(() => null)
        );
      }),
      catchError(() => of(null))
    );

    try {
      await Promise.race([
        firstValueFrom(deactivate$),
        new Promise<void>((resolve) => setTimeout(resolve, 2000)),
      ]);
    } catch {
      // Never block logout if token deactivation fails
    }
  }

  /**
   * Idempotent: for an opted-in device with a logged-in PWA user, ensure
   * OneSignal.externalId === uid and the current subscription is persisted
   * via insertOneSignalSubscriptionId / updateOneSignalToken.
   */
  public reconcileIdentityAndSubscription(): void {
    if (this.reconcileInFlight) {
      return;
    }
    if (!this.oneSignal?.User?.PushSubscription?.optedIn) {
      return;
    }

    this.reconcileInFlight = true;
    this.commonService.getUid("OneSignalService-reconcile").pipe(
      take(1),
      switchMap((uid) => {
        if (uid) {
          return of(uid);
        }
        return timer(500).pipe(
          switchMap(() =>
            this.commonService.getUid("OneSignalService-reconcile-retry").pipe(take(1))
          )
        );
      }),
      switchMap((uid) => {
        if (!uid) {
          const authService = this.injector.get(AuthService || null);
          authService?.errorHandlerLogs("User ID not found during OneSignal reconcile");
          return of(null);
        }

        const currentExternalId = this.oneSignal?.User?.externalId;
        if (currentExternalId !== uid) {
          this.oneSignal.login(uid);
        }

        const subscriptionId = this.oneSignal?.User?.PushSubscription?.id;
        if (!subscriptionId) {
          return of(null);
        }
        this.subscriptionId = subscriptionId;

        const deviceId =
          CommonVariable.FingerprintJSConst || localStorage.getItem("fingerSalt");
        const oneSignalId =
          (this.oneSignal?.User as Data)?.onesignalId || subscriptionId;

        const detailsRef = doc(
          this.firestore,
          `users/${uid}/onesignal-push-notifications/details`
        );

        return from(getDoc(detailsRef)).pipe(
          switchMap((snapshot) => {
            const data = snapshot?.data() as Data;
            const tokens = data?.token_details || [];
            const hasSubscription =
              Array.isArray(tokens) &&
              tokens.some(
                (t: Data) =>
                  t?.player_id === subscriptionId ||
                  t?.subscription_id === subscriptionId ||
                  t?.fingerprint_id === deviceId ||
                  t?.onesignal_id === deviceId
              );

            if (hasSubscription) {
              return of(null);
            }

            const oneSignalData = {
              subscription_id: subscriptionId,
              token_status: 1,
              onesignal_id: oneSignalId,
            };
            this.updateOneSignalToken(oneSignalData);
            return this.insertOneSignalSubscriptionId(deviceId, subscriptionId);
          })
        );
      }),
      catchError((error) => {
        console.error("Error reconciling OneSignal identity:", error);
        return of(null);
      }),
      finalize(() => {
        this.reconcileInFlight = false;
      })
    ).subscribe();
  }

  public updateOneSignalToken(oneSignalData: Data, useExistingData?: boolean) {
    this.updateOneSignalToken$(oneSignalData, useExistingData).subscribe({
      error: (error) => {
        console.error("Error updating OneSignal token:", error);
      },
    });
  }

  private updateOneSignalToken$(oneSignalData: Data, useExistingData?: boolean): Observable<Data> {
    const functions = getFunctions();
    functions.region = AuthService.FIREBASE_REGION;
    const callAbleFunction = httpsCallable(functions, "apiv2");
    if (useExistingData) {
      oneSignalData = {
        subscription_id: this.subscriptionId,
        token_status: 0,
        onesignal_id: this.subscriptionId,
      };
    }

    return this.requestBodyService.getOneSignalTokenRequest(oneSignalData).pipe(
      take(1),
      switchMap((requestBody) => {
        if (!requestBody) {
          return throwError(() => new Error("OneSignal token request body unavailable"));
        }
        return from(callAbleFunction(requestBody));
      }),
      map((res) => res?.data || res),
      switchMap((res) => {
        if (!this.isSuccessfulApiResult(res)) {
          return throwError(() => new Error("OneSignal token update failed"));
        }
        return of(res);
      })
    );
  }

  public promptForValidPages() {
    const currentRoute = this.router.url?.split("/")?.pop()?.split("?")?.[0];
    const validPages = ["home", "store", "game", "syndicate", "social"];

    if (validPages?.includes(currentRoute)) {
      this.promptPush();
    }
  }

  // Displays the notification permission prompt
  public promptPush(): void {
    this.oneSignal?.Slidedown?.promptPush();
  }

  // Insert the OneSignal subscription ID into Firestore
  private insertOneSignalSubscriptionId(deviceId: string, subscriptionId: string) {
    const functions = getFunctions();
    functions.region = AuthService.FIREBASE_REGION;
    const callAbleFunction = httpsCallable(functions, "apiv2");
    return this.commonService.getUid("OneSignalService").pipe(
      take(1),
      switchMap((userId) => {
        if (!userId) {
          throw new Error("UserId not found");
        }
        return from(
          callAbleFunction(
            this.requestBodyService.insertOnesignalSubscriptionId(
              userId,
              deviceId,
              subscriptionId
            )
          )
        );
      }),
      map((res) => res?.data || res),
      switchMap((res) => {
        if (!this.isSuccessfulApiResult(res)) {
          return throwError(() => new Error("Insert OneSignal subscription failed"));
        }
        return of(res);
      }),
      catchError((err) => {
        console.error("Error inserting OneSignal subscription:", err);
        return throwError(() => err);
      })
    );
  }


  // Update the user's push notification status / award free lines via apiv2
  private updatePushNotificationStatus(userId: string): Observable<PushOptInResult> {
    const functions = getFunctions();
    functions.region = AuthService.FIREBASE_REGION;
    const callAbleFunction = httpsCallable(functions, "apiv2");

    return from(callAbleFunction(this.requestBodyService.pushNotificationRequest(userId)))
      .pipe(
        map((res) => res?.data || res),
        switchMap((res) => {
          if (this.isAlreadyIssuedPayload(res)) {
            return of({ status: "already_issued" as const, raw: res });
          }
          if (!this.isSuccessfulApiResult(res)) {
            return throwError(() => new Error("Push notification status update failed"));
          }
          return of({ status: "success" as const, raw: res });
        }),
        catchError((error) => {
          if (this.isAlreadyIssuedPayload(error)) {
            return of({ status: "already_issued" as const, raw: error });
          }
          console.error("Error updating push notification status:", error);
          return throwError(() => error);
        })
      );
  }

  /** Detect "prize already issued" from callable success body or error payload */
  private isAlreadyIssuedPayload(payload: unknown): boolean {
    try {
      const parts: string[] = [];
      if (typeof payload === "string") {
        parts.push(payload);
      } else if (payload instanceof Error) {
        // JSON.stringify(Error) is "{}" — read message/code/details explicitly
        parts.push(payload.message);
        const extra = payload as Error & { code?: unknown; details?: unknown };
        if (typeof extra.code === "string") {
          parts.push(extra.code);
        }
        if (typeof extra.details === "string") {
          parts.push(extra.details);
        } else if (extra.details != null) {
          parts.push(JSON.stringify(extra.details));
        }
      } else if (payload && typeof payload === "object") {
        const obj = payload as Record<string, unknown>;
        for (const key of ["message", "code", "details"]) {
          const value = obj[key];
          if (typeof value === "string") {
            parts.push(value);
          } else if (value != null) {
            parts.push(JSON.stringify(value));
          }
        }
        parts.push(JSON.stringify(payload));
      }
      return parts.join(" ").toLowerCase().includes("already issued");
    } catch {
      return false;
    }
  }

  public setUserCurrencyTag(currency: string) {
    if (!currency) return;
    try {
      this.oneSignal?.User?.addTag("currency", currency);
    } catch (err) {
      this.errorHandlerLogs("Error setting currency tag", err);
    }
  }
  errorHandlerLogs(message: string, error?: Data): { message: string; timestamp: string; error: Data } {
    const log = {
      message,
      timestamp: new Date().toISOString(),
      error: error ? this.formatError(error) : null,
    };
    return log;

  }
  private formatError(error: Data): Data {
    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
    }
  }

  public getV2CloudFunction(CloudFunctionName: string, payload: Data | string, options: Data = {}): Observable<any> {
    if (!navigator.onLine) {
      return new Observable(observer => {
        const resolver = {
          resolve: (result: Data) => observer.next(result),
          reject: (error: Data) => observer.error(error)
        };
        this.offlineQueue.push({ name: CloudFunctionName, payload, options, resolver });
        // Process offline queue when online
        window.addEventListener('online', () => {
          this.processOfflineQueue();
        }, { once: true });
      });
    }

    try {
      if (!navigator.onLine) {
        return throwError(() => new Error('OFFLINE_ERROR'));
      }

      const region = AuthService.FIREBASE_REGION || 'us-central1';
      const callable = httpsCallable(getFunctions(undefined, region), CloudFunctionName, options);


      return defer(() => from(callable(payload))).pipe(takeUntil(this.destroy$),
        tap(),

        map((res: Data) => {
          return res?.data;
        }),

        retryWhen((errors) =>
          errors.pipe(takeUntil(this.destroy$),
            tap((error) => {
              const msg = error?.message?.toLowerCase?.() || '';
              const isNetworkError = msg.includes('offline') || msg.includes('network');

              if (isNetworkError && !navigator.onLine) {
              }
            }),

            scan((retryCount, error) => {
              if (retryCount >= 3) {
                console.error(`Max retries reached for ${CloudFunctionName}`);
                throw error;
              }

              console.warn(`Retrying ${CloudFunctionName} attempt ${retryCount + 1}`);
              return retryCount + 1;
            }, 0),
            delayWhen(retryCount => timer(Math.min(Math.pow(2, retryCount) * 1000, 30000))) // Exponential backoff capped at 30s
          )
        ),
        catchError(error => {
          if (error?.message === 'OFFLINE_ERROR') {
            return throwError(() => new Error('Network offline. Please check your connection.'));
          }

          if (error?.message === 'UNAUTHENTICATED_ERROR') {
            return throwError(() => new Error('User authentication lost. Please log in again.'));
          }

          console.error(`Cloud function ${CloudFunctionName} failed:`, error);
          return throwError(() => new Error(`Error calling ${CloudFunctionName}: ${error?.message || error}`));
        })
      );
    } catch (error) {
      console.error(`Unexpected error in getV2CloudFunction:`, error);
      return throwError(() => new Error(`Unexpected error: ${error?.message || error}`));
    }
  }


  //Offline queue
  private processOfflineQueue() {
    if (!navigator.onLine) return;

    while (this.offlineQueue.length) {
      const { name, payload, options, resolver } = this.offlineQueue.shift()!;
      this.getV2CloudFunction(name, payload, options).subscribe({
        next: (result) => resolver.resolve(result),
        error: (error) => resolver.reject(error)
      });
    }
  }
  // Offline queue
  private offlineQueue: Array<{
    name: string;
    payload: Data | string;
    options: Data;
    resolver: { resolve: Function; reject: Function };
  }> = [];

}
